import useComponentsV2List, {
  joinCodesForComponentsV2Filter,
} from 'elements/hardwareSelectorV2/fetch/useComponentsV2List'
import { usePublicFeatureConfig } from 'hooks/usePublicFeatureConfig'
import { useMemo } from 'react'

export interface ProductCarouselConfig {
  title: string
  description: string
  productCodes: string[]
  order: number
  enabled: boolean
  viewAllLink?: string
}

export interface ProductCarouselEntry {
  config: ProductCarouselConfig
  componentsData: any[]
  viewAllLink?: string
}


const useHomeProductCarouselData = (countryCode: string) => {
  // Why? feature-config-driven product carousel system to support curated product listings in Shop when personalisation data is unavailable (or as a complement to it).
  // This fetches public config containing product codes and then retrieved the data via componentsv2 endpoint.
  // Consumed in OrderingHome to dynamically display the components
  const carouselData = usePublicFeatureConfig('shop_product_carousel_control') as
    | Record<string, ProductCarouselConfig[]>
    | undefined

  const enabledConfigs = useMemo(() => {
    const configs = carouselData?.[countryCode] || []
    return configs.filter((c) => c.enabled).sort((a, b) => a.order - b.order)
  }, [carouselData, countryCode])

  const allCodes = useMemo(() => {
    const codes = new Set<string>()
    enabledConfigs.forEach((c) => c.productCodes.forEach((code) => codes.add(code)))
    return Array.from(codes)
  }, [enabledConfigs])

  const codesFilter = useMemo(() => joinCodesForComponentsV2Filter(allCodes), [allCodes])
  const perPage = Math.max(allCodes.length, 1)

  const { componentsData, loading } = useComponentsV2List({
    filterValues: { codes: codesFilter },
    perPage,
    blockRequest: allCodes.length === 0,
  })

  const componentsMap = useMemo(() => {
    const map: Record<string, any> = {}
    componentsData.forEach((component: any) => {
      if (component?.code) map[component.code] = component
    })
    return map
  }, [componentsData])

  const carouselEntries: ProductCarouselEntry[] = useMemo(() => {
    return enabledConfigs.map((config) => {
      const codes = config.productCodes.map((code) => componentsMap[code]).filter(Boolean)
      return {
        config,
        componentsData: codes,
        viewAllLink: '/shop/search',
      }
    })
  }, [enabledConfigs, componentsMap])

  return { carouselEntries, isLoading: loading }
}

export default useHomeProductCarouselData
